/**
 * Request interceptor: for any request whose URL contains `/v25/`, attach the
 * `user_role` selected by the user (avatar dropdown) onto the outgoing payload.
 *
 * - POST/PUT/PATCH with JSON body: merge `user_role` into the body.
 * - GET / DELETE with query params: merge into `params`.
 * - multipart/form-data (FormData): append `user_role` field.
 *
 * Falls back to `localStorage.role` if no explicit selection has been made yet.
 */
import axios from "axios";

const V25_MARKER = "/v25/";

const isV25Url = (config) => {
  const url = (config?.url || "") + "";
  if (url.indexOf(V25_MARKER) !== -1) return true;
  const baseURL = (config?.baseURL || "") + "";
  if (baseURL.indexOf(V25_MARKER) !== -1) return true;
  return false;
};

// Roles that should be reported to the backend as a plain "USER" on the
// outgoing payload, regardless of what the user picked in the avatar dropdown.
const ROLES_SENT_AS_USER = new Set([
  "OWNER",
  "ESCALATOR1",
  "ESCALATOR2",
  "ESCALATOR3",
]);

const mapUserRoleForPayload = (role) => {
  if (!role) return role;
  const normalized = String(role).trim().toUpperCase();
  return ROLES_SENT_AS_USER.has(normalized) ? "USER" : role;
};

const resolveUserRole = () =>
  mapUserRoleForPayload(
    localStorage.getItem("selected_user_role") ||
      localStorage.getItem("role") ||
      null
  );

axios.interceptors.request.use(
  (config) => {
    if (!isV25Url(config)) return config;
    const userRole = resolveUserRole();
    if (!userRole) return config;

    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (!config.data.has("user_role")) {
        config.data.append("user_role", userRole);
      }
      return config;
    }

    const method = (config.method || "get").toLowerCase();
    if (method === "get" || method === "delete") {
      config.params = { ...(config.params || {}), user_role: userRole };
      return config;
    }

    if (config.data == null) {
      config.data = { user_role: userRole };
    } else if (typeof config.data === "string") {
      try {
        const parsed = JSON.parse(config.data);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          parsed.user_role = userRole;
          config.data = JSON.stringify(parsed);
        }
      } catch (e) {
        // Non-JSON string payload (rare) — leave it untouched.
      }
    } else if (typeof config.data === "object" && !Array.isArray(config.data)) {
      config.data = { ...config.data, user_role: userRole };
    }
    return config;
  },
  (error) => Promise.reject(error)
);
